import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { OverlayPosition, OverlayRole } from '../fc-overlay/overlay.types';
import { OverlayService } from '../fc-overlay/overlay.service';

@Component({
  selector: 'fc-menu-content',
  template: `
    <div
      class="flex min-w-150 flex-col gap-2 shadow-lg rounded-lg border border-secondary-100 bg-white p-4"
    >
      <ng-content />
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    popover: 'manual',
    class: 'fc-overlay',
    '[attr.data-position]': 'position()',
    '[attr.role]': 'role()',
    '(toggle)': 'closeNestedMenusWhenClosed($event)',
    '(keydown.escape)': 'closeOnEscape($event)',
  },
})
export class MenuContentComponent implements AfterViewInit, OnDestroy {
  private elementRef = inject(ElementRef<HTMLElement>);
  private overlayService = inject(OverlayService);
  private overlayId?: string;

  target = input<any>();
  position = input<OverlayPosition>('bottom');
  role = input<OverlayRole>('menu');

  protected closeOnEscape(event: Event): void {
    const element: HTMLElement = this.elementRef.nativeElement;
    if (!element.matches(':popover-open')) return;
    event.stopPropagation();
    this.target()?.close();
  }

  // A nested manual popover outlives its parent's close; left open, it sits
  // below the reopened parent in the top layer and loses its anchor.
  protected closeNestedMenusWhenClosed(event: Event): void {
    if ((event as ToggleEvent).newState !== 'closed') return;
    const element: HTMLElement = this.elementRef.nativeElement;
    element
      .querySelectorAll<HTMLElement>('[popover]:popover-open')
      .forEach((nestedMenu) => nestedMenu.hidePopover());
  }

  ngAfterViewInit(): void {
    const trigger = this.target();
    if (!trigger) return;

    const overlayId = trigger.overlayId;
    if (!overlayId) return;

    const element = this.elementRef.nativeElement;
    element.id = overlayId;
    element.style.positionAnchor = `--${overlayId}`;
    this.overlayService.register(overlayId, element);
    this.overlayId = overlayId;
  }

  ngOnDestroy(): void {
    if (this.overlayId) this.overlayService.unregister(this.overlayId);
  }
}
